import { Link, useLocation } from "wouter";
import { ShoppingCart, Sun, Moon, Menu, X, User, LogOut, Settings, Package, Shield, MessageCircle } from "lucide-react";
import logoImg from "@assets/logo.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useCart } from "@/lib/cart";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/hooks/use-auth";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

export function Header() {
  const { totalItems } = useCart();
  const { theme, toggleTheme } = useTheme();
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: unreadCounts = {} } = useQuery<Record<number, number>>({
    queryKey: ["/api/orders/unread-counts"],
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 20000 : false,
  });
  const totalUnread = Object.values(unreadCounts as Record<number, number>).reduce((a, b) => a + b, 0);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/templates", label: "Order Flow Tools" },
    { href: "/indicators", label: "Indicators" },
  ];

  const renderAuthControls = () => {
    if (isLoading) {
      return <Skeleton className="h-9 w-20 rounded-md" />;
    }

    if (!isAuthenticated) {
      return (
        <Link href="/auth">
          <Button variant="ghost" data-testid="button-sign-in">
            <User className="w-4 h-4 mr-2" />
            Sign In
          </Button>
        </Link>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" data-testid="button-user-menu">
            <User className="w-4 h-4 mr-2" />
            {user?.firstName || "Account"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link href="/purchases">
            <DropdownMenuItem data-testid="link-purchases">
              <Package className="w-4 h-4 mr-2" />
              My Purchases
            </DropdownMenuItem>
          </Link>
          <Link href="/messages">
            <DropdownMenuItem data-testid="link-messages">
              <MessageCircle className="w-4 h-4 mr-2" />
              <span className="flex-1">Messages</span>
              {totalUnread > 0 && (
                <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                  {totalUnread}
                </span>
              )}
            </DropdownMenuItem>
          </Link>
          <Link href="/settings">
            <DropdownMenuItem data-testid="link-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          {user?.isAdmin && (
            <>
              <Link href="/admin">
                <DropdownMenuItem data-testid="link-admin">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => logout()} data-testid="button-sign-out">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md" role="banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          <Link href="/" data-testid="link-home">
            <div className="flex items-center gap-2 cursor-pointer">
              <img src={logoImg} alt="Sierra Chart Pro" className="h-14 object-contain" />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-lg font-bold tracking-tight">
                  <span className="text-gray-400">Sierra</span><span className="text-gray-400">Chart</span><span className="text-primary">Pro</span><span className="text-gray-400">.com</span>
                </span>
                <span className="text-xs text-muted-foreground tracking-wide">Trading Templates &amp; Tools</span>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <Button
                  variant={location === link.href ? "secondary" : "ghost"}
                  data-testid={`link-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={toggleTheme}
              data-testid="button-theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="sr-only">Toggle theme</span>
            </Button>

            {renderAuthControls()}

            <Link href="/cart">
              <Button variant="ghost" className="relative" data-testid="link-cart">
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Cart</span>
                {totalItems > 0 && (
                  <Badge className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-xs px-1.5">
                    {totalItems}
                  </Badge>
                )}
              </Button>
            </Link>

            <Button
              size="icon"
              variant="ghost"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <Button
                    variant={location === link.href ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`link-mobile-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
              {isAuthenticated && (
                <>
                  <Link href="/purchases">
                    <Button
                      variant={location === "/purchases" ? "secondary" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="link-mobile-purchases"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      My Purchases
                    </Button>
                  </Link>
                  <Link href="/messages">
                    <Button
                      variant={location === "/messages" ? "secondary" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="link-mobile-messages"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Messages
                      {totalUnread > 0 && (
                        <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                          {totalUnread}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Link href="/settings">
                    <Button
                      variant={location === "/settings" ? "secondary" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="link-mobile-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Button>
                  </Link>
                  {user?.isAdmin && (
                    <Link href="/admin">
                      <Button
                        variant={location === "/admin" ? "secondary" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="link-mobile-admin"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
